import { createContext, useContext, useState } from 'react';

export type CookieConsent = 'accepted' | 'refused';

type CookieConsentContextValue = {
    consent: CookieConsent | null;
    chooseConsent: (choice: CookieConsent) => void;
    openSettings: () => void;
};

export const COOKIE_CONSENT_KEY = 'flag-cookie-consent-v1';

export const readCookieConsent = (): CookieConsent | null => {
    try {
        const stored = window.localStorage.getItem(COOKIE_CONSENT_KEY);
        return stored === 'accepted' || stored === 'refused' ? stored : null;
    } catch {
        return null;
    }
};

const CookieConsentContext = createContext<CookieConsentContextValue | null>(null);

export const CookieConsentProvider: React.FC<{children: React.ReactNode}> = ({ children }) => {
    const [consent, setConsent] = useState<CookieConsent | null>(() => readCookieConsent());

    const chooseConsent = (choice: CookieConsent) => {
        try {
            window.localStorage.setItem(COOKIE_CONSENT_KEY, choice);
        } catch {
            // The in-memory choice still applies when storage is unavailable.
        }
        setConsent(choice);
    };

    return (
        <CookieConsentContext.Provider value={{consent, chooseConsent, openSettings: () => setConsent(null)}}>
            {children}
        </CookieConsentContext.Provider>
    );
};

export const useCookieConsent = () => {
    const context = useContext(CookieConsentContext);
    if (!context) throw new Error('useCookieConsent must be used inside CookieConsentProvider');
    return context;
};
